import DeviceGroup from "@/classes/DeviceGroup";
import axios from "axios";
import { ActionTree } from "vuex";
import { GlobalInterface } from "../index.js";
import { StateInterface } from "./state";
import Device from "@/classes/Device";
import sharedMethods from "@/sharedMethods.js";

const actions: ActionTree<StateInterface, GlobalInterface> = {
  /**
   * fetch all device groups for current account
   * @see https://api-dev.mirrads.de/api-docs/#/Devicemanagement/get_api_getDeviceGroupsV2
   * @returns groups
   */
  async fetchDeviceGroups({ commit, rootState }) {
    const AccID = rootState.user.AccID;

    const response = await axios.get(
      rootState.api + `getDeviceGroupsV2?accId=${AccID}`
    );

    // parse objects to ts classes
    let groups: DeviceGroup[] = [];
    response.data.forEach(
      (groupObject: { GID: number; GNAME: string; ORIENTATION: string; devices: any[] }) => {
        groups.push(DeviceGroup.fromObject(groupObject));
      }
    );
    commit("setDeviceGroups", groups);

    return groups;
  },
  /**
   * get all device states/heartbeats
   * @see https://api-dev.mirrads.de/api-docs/#/Devicemanagement/get_api_devices_current_states
   * @returns response
   */
  async fetchDeviceStates({ rootState }) {
    return axios.get(rootState.api + "devices/current-states/", {
      params: { accid: rootState.user.AccID },
    });
  },
  /**
   * add a new devie group
   * @param groupName name of the new group
   * @returns response
   */
  async addDeviceGroup({ commit, rootState }, groupName: string) {
    const info = {
      AccID: rootState.user.AccID,
      groupName: groupName,
    };

    return await axios
      .post(rootState.api + "createDeviceGroup/", info)
      .then((response) => {
        if (response.status === 200) {
          const newGroup = DeviceGroup.fromObject(response.data.group);
          commit("addDeviceGroup", newGroup);
        }
      });
  },

  /**
   * deletes a device group
   * @param groupID id of the group to delete
   * @returns response
   * @see https://api-dev.mirrads.de/api-docs/#/Devicemanagement/delete_api_devices_device_group
   */
  async deleteDeviceGroupByID({ rootState, commit }, groupID: string) {
    return new Promise((resolve, reject) => {
      axios
        .delete(rootState.api + "devices/device-group", {
          data: { groupID: groupID },
        })
        .then((response) => {
          commit("deleteDeviceGroup", groupID);
          resolve(response);
        })
        .catch((error) => {
          resolve(error);
        });
    });
  },

  /**
   * Get available device versions
   * @returns response
   */
  async getDeviceVersions({ rootState }) {
    return (await axios.get(rootState.updateUrl + "/versions")).data;
  },

  /**
   * assigns a device to the given group
   * @param payload must contain deviceId, groupId
   * @returns response
   */
  async changeDeviceGroupAssignment(
    { rootState, dispatch },
    payload: { deviceId: string; groupId: string }
  ) {
    // its ok to fetch again, because we do this already in an interval
    // (one request doesnt make a difference)
    const response = await axios.post(
      rootState.api + "updateDeviceGroup/",
      payload
    );
    dispatch("fetchDeviceGroups");
    return response;
  },

  /**
   * Update Device Version
   * @returns response
   */
  async updateTargetVersions({ rootState, commit }, data: { deviceId: number, versionmp: string, versionsm: string }) {
    try {
      const response = await axios.get(rootState.api + "devices/update-version", { params: data });
      if(response.status == 200) {
        commit("updateTargetVersions", data);
        sharedMethods.successNotify('Update scheduled. Now pending. Update may take a couple of minutes. You can check later if the update was successful.');
      }
      return response;
    } catch(err) {
      sharedMethods.errorNotify('Updating version failed.');
    }
  },

  /**
   * reboots a device
   * @param deviceId id of device that should be rebooted
   * @returns response
   */
  async rebootDeviceByID({ rootState }, deviceId: string) {
    return await axios.post(rootState.api + "devices/reboot-device", deviceId);
  },

  /**
   * adds a new devie to the account and given group id
   * @param payload
   */
  async addDevice(
    { rootState, dispatch },
    payload: {
      deviceName: string;
      deviceCode: string;
      groupId: number;
    }
  ) {
    let error = null;
    await axios
      .post(rootState.api + "addDevice/", payload)
      .then(async (response) => {
        if (response.status === 200) {
          // refetch here again because necessary properties are set server side
          await dispatch("fetchDeviceGroups");
          sharedMethods.successNotify(response.data);
        }
      })
      .catch((err) => {
        error = "Unknown error, check your input data";

        if (err.status === 400) {
          if (err.data === "invalid Name") {
            error = "This name is already in use";
          } else if (err.data === "invalid Code") {
            error = "A device with this code could not be found";
          }
        }
      });

    return error;
  },

  /**
   * update the config of a device
   * @param payload must contain id and device settings object
   * @returns response
   * @see https://api-dev.mirrads.de/api-docs/#/Devicemanagement/post_api_devices_device_config
   * @see Device.toDeviceSettingsObject()
   */
  async changeDeviceConfigByID({ rootState, commit }, newDeviceSettings) {
    commit("changeDeviceConfig", newDeviceSettings);
    const response = await axios.post(
      rootState.api + "devices/device-config/",
      newDeviceSettings
    );
    return response;
  },

  /**
   * changes the connectpath of a device (must be called to avoid a endpoint calling loop from the device)
   */
  async changeDeviceConnectpath({ rootState, commit }, device: Device) {
    // change device connectpath via mutation
    commit("changeDeviceConnectpath", device);

    const msg = {
      identifier: device.IDENTIFIER,
      connectPath: device.CONNECTPATH,
    };
	try {
		await Promise.all([
			axios.post(rootState.backendDevUrl + "/api/devices/change-connect-path", msg),
			axios.post(rootState.backendQualityUrl + "/api/devices/change-connect-path", msg),
			axios.post(rootState.liveClusterA + "/api/devices/change-connect-path", msg),
			axios.post(rootState.liveClusterB + "/api/devices/change-connect-path", msg)
		]);
		return {
			status: 200,
			msg: 'Updated sucessfully'
		}
	} catch(err) {
		return {
			status: 500,
			msg: 'Update failed for one or many Backend instances.'
		}
	}

  },

  /**
   * edit device settings (set new device name)
   * @param payload must contain  deviceID, deviceName
   * @returns response
   */
  async editDeviceByID({ rootState, commit }, payload) {
    payload.AccID = rootState.user.AccID;
    commit("editDevice", payload);
    return await axios.post(rootState.api + "editDevice/", payload);
  },

  /**
   * deletes a device
   * @param deviceId
   * @returns response
   */
  async deleteDeviceByID({ rootState, commit }, deviceId: number) {
    const info = {
      deviceId: deviceId,
      AccID: rootState.user.AccID,
    };

    const response = await axios.post(rootState.api + "deleteDevice/", info);
    if (response.status === 200) {
      // commit only after successfull delete process to ensure a consistent system
      commit("deleteDevice", deviceId);
    }
    return response;
  },
  /**
   * Updates the device heredity setting of a device to any of the valid values 'device', 'group' or 'deviceAndGroup'
   * @param { deviceId: number, value: string }
   * @returns response
   */
  async updatePlaylistHeredity({ rootState, commit }, payload: { deviceId: number, playlistHeredity: string }) {
    try {
      const response = await axios.put(rootState.api + "devices/set-playlist-heredity", { deviceId: payload.deviceId, playlistHeredity: payload.playlistHeredity });
      if (response.status === 200) {
        // commit only after successfull delete process to ensure a consistent system
        commit("updatePlaylistHeredity", payload);
        return response;
      } else {
        return 'An error occured updating the playlist heredity';
      }
    } catch(err) {
      console.log(err);
      return 'An error occured updating the playlist heredity';
    }
  },

  /**
   * Updates 
   * @param { accId: number, groupId: number, groupName: string }
   * @returns response
   */
  async updateGroupName({ rootState, commit }, payload: { accId: number, groupId: number, groupName: string }) {
    try {
      const response = await axios.put(rootState.api + "devices/set-group-name", payload);
      if (response.status === 200) {
        // commit only after successfull delete process to ensure a consistent system
        commit("updateGroupName", payload);
        return response;
      } else {
        return 'An error occured updating the playlist heredity';
      }
    } catch(err) {
      console.log(err);
      return 'An error occured updating the playlist heredity';
    }
  },

  /**
   * Updates 
   * @param payload { groupId: number, settings: { orientation: string } }
   * @returns response
   */
  async updateGroupSettings({ rootState, commit }, payload: { groupId: number, settings: { orientation: string } }) {
    try {
      const response = await axios.put(rootState.api + "devices/group-settings", payload);
      if (response.status === 200) {
        // commit only after successfull delete process to ensure a consistent system
        commit("updateGroupSettings", payload);
        return response;
      } else {
        return 'An error occured updating the playlist heredity';
      }
    } catch(err) {
      console.log(err);
      return 'An error occured updating the playlist heredity';
    }
  },
};

export default actions;
